<template>
  <div id="candidate-profile">
    <edit-profile-modal
      :modal-is-active="editModalIsActive"
      :main-details="editProfile.mainDetails"
      :bio="editProfile.bio"
      :accomplishments="editProfile.accomplishments"
      :skills="editProfile.skills"
      :companies="editProfile.companies"
      :industries="editProfile.industries"
      :education="editProfile.education"
      @toggleModal="toggleEditModal"
    />
    <section
      v-if="isUserCandidate"
      class="section no-pad-bottom"
    >
      <ProfileScore />
    </section>
    <section
      v-if="isUserCandidate"
      class="section"
    >
      <div
        class="button is-text"
        @click="toggleProfileEditMode()"
      >
        <div
          class="button is-rounded main-edit-profile"
          :class="isEditModeActive ? 'is-success' : 'is-primary'"
        >
          <i class="fas fa-pencil-alt" />
        </div>
        <h5
          v-if="!isEditModeActive"
          class="title is-5 is-inline"
        >
          Edit Profile
        </h5>
        <h5
          v-else
          class="title is-5 is-inline"
        >
          Done Editing
        </h5>
      </div>
    </section>
    <!--<section class="section">
            <h4 class="title is-4">
                Confidential Employment Profile
            </h4>
    </section>-->
    <section class="section">
      <div class="columns is-desktop">
        <!-- MAIN DETAILS -->
        <div class="column">
          <div class="card">
            <div class="card-content">
              <div
                v-if="isUserCandidate && isEditModeActive"
                class="edit-icon"
                @click="toggleEditModal('mainDetails')"
              >
                <i class="fas fa-pencil-alt" />
              </div>
              <h4 class="title is-4">
                <strong>{{ profile.username }}</strong>
              </h4>
              <p
                v-if="profile.profession.name"
                class="is-size-5 subtitle title"
              >
                {{ profile.profession.name }}
                <span
                  class="is-size-7 has-text-grey"
                >&nbsp;({{ profile.profession.years_experience }})</span>
              </p>
              <p
                v-else
                class="has-text-grey-light"
              >
                Add your profession
              </p>
              <ul
                v-if="isUserCandidate && profile.location"
                class="subtitle"
              >
                <li>
                  <i class="fas fa-map-marker-alt fa-xs map-marker-icon" />
                  <p class="is-size-7 has-text-primary has-text-weight-bold is-inline">
                    {{ profile.location.undisclosed ? 'Location Undisclosed' : profile.location.current }}
                    <span
                      v-if="profile.location.open_move"
                      class="is-size-7 has-text-weight-normal has-text-grey"
                    >&nbsp;&nbsp;Open To Relocation</span>
                  </p>
                </li>
              </ul>
              <ul
                v-else-if="!isUserCandidate && !profile.location"
                class="subtitle"
              >
                <li>
                  <i class="fas fa-map-marker-alt fa-xs map-marker-icon" />
                  <p class="is-size-7 has-text-primary has-text-weight-bold is-inline">
                    Location Undisclosed
                  </p>
                </li>
              </ul>
              <ul
                v-else-if="!isUserCandidate && profile.location"
                class="subtitle"
              >
                <li>
                  <i class="fas fa-map-marker-alt fa-xs map-marker-icon" />
                  <p class="is-size-7 has-text-primary has-text-weight-bold is-inline">
                    {{ profile.location.undisclosed ? 'Location Undisclosed' : profile.location.current }}
                    <span
                      v-if="profile.location.open_move"
                      class="is-size-7 has-text-weight-normal has-text-grey"
                    >&nbsp;&nbsp;Open To Relocation</span>
                  </p>
                </li>
              </ul>
              <p
                v-else
                class="has-text-grey-light"
              >
                Add a Location
              </p>
              <div id="top-skills">
                <span
                  v-for="skill in topSkills"
                  :key="skill.key"
                  class="tag is-rounded is-primary"
                >{{ skill }}</span>
              </div>
            </div>
          </div>
        </div>
        <!-- BIO -->
        <div class="column">
          <div class="card">
            <div class="card-content">
              <div
                v-if="isUserCandidate && isEditModeActive"
                class="edit-icon"
                @click="toggleEditModal('bio')"
              >
                <i class="fas fa-pencil-alt" />
              </div>
              <h4 class="title is-4">
                <strong>About</strong>
              </h4>
              <p
                v-if="profile.bio"
                class="line-height-is-large"
              >
                {{ profile.bio }}
              </p>
              <p
                v-else
                class="has-text-grey-light"
              >
                Let companies know what you're about.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ACCOMPLISHMENTS -->
    <section class="section">
      <div class="card">
        <header class="card-header">
          <div
            v-if="isUserCandidate && isEditModeActive"
            class="edit-icon"
            @click="toggleEditModal('accomplishments')"
          >
            <i class="fas fa-pencil-alt" />
          </div>
          <h4 class="title is-4 card-header-title">
            Accomplishments
          </h4>
        </header>
        <div class="card-content content">
          <ul
            v-if="profile.accomplishments.length > 0"
            class="no-margin-top line-height-is-large"
          >
            <li
              v-for="accomplishment in profile.accomplishments"
              :key="accomplishment.key"
            >
              {{ accomplishment }}
            </li>
          </ul>
          <p
            v-else
            class="has-text-grey-light"
          >
            Tell employers about your achievements in the workplace.
          </p>
        </div>
      </div>
    </section>

    <!-- INDUSTRIES -->
    <section class="section">
      <div class="card">
        <header class="card-header">
          <div
            v-if="isUserCandidate && isEditModeActive"
            class="edit-icon"
            @click="toggleEditModal('industries')"
          >
            <i class="fas fa-pencil-alt" />
          </div>
          <h4 class="title is-4 card-header-title">
            Industries
          </h4>
        </header>
        <div class="card-content">
          <div
            v-if="profile.industries.current.length > 0"
            class="columns is-desktop"
          >
            <div class="column">
              <div
                v-for="industry in profile.industries.current"
                :key="industry.key"
                class="columns is-mobile is-marginless is-vcentered"
              >
                <div class="column no-pad-left-right is-8-tablet is-8-desktop is-8-mobile">
                  <p class="title is-6 has-text-grey-dark">
                    {{ industry.industry }}
                  </p>
                  <percent-bar :amount-completed="industry.years_experience" />
                </div>
                <div class="column no-pad-bottom">
                  <span class="is-size-7 has-text-grey">({{ industry.years_experience }} Years)</span>
                </div>
              </div>
            </div>
          </div>
          <p
            v-else
            class="has-text-grey-light"
          >
            Show your strengths by highlighting key industries you have experience with.
          </p>
        </div>
      </div>
    </section>

    <!-- SKILLS -->
    <section class="section">
      <div class="card">
        <header class="card-header">
          <div
            v-if="isUserCandidate && isEditModeActive"
            class="edit-icon"
            @click="toggleEditModal('skills')"
          >
            <i class="fas fa-pencil-alt" />
          </div>
          <h4 class="title is-4 card-header-title">
            Skills
          </h4>
        </header>
        <div class="card-content">
          <div v-if="profile.skills.length > 0">
            <div
              v-for="chunk in chunkedSkills"
              :key="chunk.key"
              class="columns"
            >
              <div
                v-for="skill in chunk"
                :key="skill.key"
                class="column has-text-weight-bold"
              >
                {{ skill }}
              </div>
            </div>
          </div>
          <p
            v-else
            class="has-text-grey-light"
          >
            Show off your valuable skillset to get easily discovered.
          </p>
        </div>
      </div>
    </section>

    <!-- COMPANIES -->
    <section class="section">
      <div class="card">
        <header class="card-header">
          <div
            v-if="isUserCandidate && isEditModeActive"
            class="edit-icon"
            @click="toggleEditModal('companies')"
          >
            <i class="fas fa-pencil-alt" />
          </div>
          <h4 class="title is-4 card-header-title">
            Companies
          </h4>
        </header>
        <div class="card-content">
          <div v-if="profile.companies.length > 0">
            <div
              v-for="chunk in chunkedCompanies"
              :key="chunk.key"
              class="columns"
            >
              <div
                v-for="company in chunk"
                :key="company.key"
                class="column"
              >
                <div class="card degree-card">
                  <div class="card-content">
                    <p class="is-size-5 has-text-weight-bold">
                      {{ company.corporate_structure }}
                    </p>
                    <p class="has-text-weight-bold">
                      {{ company.annual_revenue }}
                      <small
                        class="company-attribute has-text-grey-light"
                      >Annual Revenue</small>
                    </p>
                    <p>
                      <small class>
                        {{ company.number_of_employees }}
                        <span
                          class="has-text-weight-bold company-attribute has-text-grey-light"
                        >Employees</span>
                      </small>
                    </p>
                    <p class>
                      <small>
                        {{ company.role }}
                        <span
                          class="has-text-weight-bold company-attribute has-text-grey-light"
                        >My Role</span>
                      </small>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p
            v-else
            class="has-text-grey-light"
          >
            Describe the types of companies you've impacted.
          </p>
        </div>
      </div>
    </section>

    <!-- EDUCATION -->
    <section
      v-if="profile.education.degrees.length > 0
        || profile.education.certifications.length > 0
        || isUserCandidate"
      class="section"
    >
      <div class="card">
        <header class="card-header">
          <div
            v-if="isUserCandidate && isEditModeActive"
            class="edit-icon"
            @click="toggleEditModal('education')"
          >
            <i class="fas fa-pencil-alt" />
          </div>
          <h4 class="title is-4 card-header-title">
            Education
          </h4>
        </header>
        <div class="card-content">
          <div class="columns">
            <div class="column">
              <h5 class="title is-5 has-text-weight-normal">
                Degrees
              </h5>
              <div v-if="profile.education.degrees.length > 0">
                <div
                  v-for="degree in profile.education.degrees"
                  :key="degree.key"
                  class="card degree-card"
                >
                  <div class="card-content">
                    <p
                      v-if="!degree.institution_is_undisclosed"
                      class="has-text-weight-bold"
                    >
                      {{ degree.institution }}
                    </p>
                    <p
                      v-else
                      class="has-text-weight-bold has-text-grey"
                    >
                      Institution Undisclosed
                    </p>
                    <p>
                      <span>{{ degree.degree_type }}</span>
                      - {{ degree.major }}
                    </p>
                    <p class="education-status">
                      <span
                        class="tag is-small is-rounded certification-tag"
                        :class="degree.complete ? 'is-success' : 'is-warning'"
                      />
                      <small
                        class="is-size-7 has-text-grey-light has-text-weight-bold"
                      >&nbsp;{{ degree.complete ? 'Complete' : 'Pursuing' }}</small>
                    </p>
                  </div>
                </div>
              </div>
              <p
                v-else
                class="has-text-grey-light"
              >
                Include any relevant schools and degrees.
              </p>
            </div>
            <div class="column">
              <h5
                v-if="profile.education.certifications.length > 0 || isUserCandidate"
                class="title is-5 has-text-weight-normal"
              >
                Credentials
              </h5>
              <div v-if="profile.education.certifications.length > 0">
                <div
                  v-for="certification in profile.education.certifications"
                  :key="certification.key"
                  class="card degree-card"
                >
                  <div class="card-content">
                    <p class="has-text-weight-bold">
                      {{ certification.institution }}
                    </p>
                    <p>{{ certification.certification_name }}</p>
                    <p class="education-status">
                      <span
                        class="tag is-small is-rounded certification-tag"
                        :class="(certification.type === 'license' && certification.active) || (certification.type === 'certification' && certification.complete) ? 'is-success' : 'is-warning'"
                      />
                      <small
                        v-if="certification.type === 'license'"
                        class="is-size-7 has-text-grey-light has-text-weight-bold"
                      >&nbsp;{{ certification.active ? 'Active' : 'Inactive' }}</small>
                      <small
                        v-else
                        class="is-size-7 has-text-grey-light has-text-weight-bold"
                      >&nbsp;{{ certification.complete ? 'Complete' : 'Pursuing' }}</small>
                    </p>
                  </div>
                </div>
              </div>
              <p
                v-else-if="profile.education.certifications.length === 0 && isUserCandidate"
                class="has-text-grey-light"
              >
                Certifications and Licenses make you qualified for niche positions. Make sure you include as many as you have.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import PercentBar from '@/components/Utilities/PercentBar';
import chunk from 'chunk';
import EditProfileModal from '@/components/CandidateProfile/EditProfileModal';
import ProfileScore from '@/components/CandidateProfile/ProfileScore';

export default {
  name: 'CandidateProfile',
  components: {
    'percent-bar': PercentBar,
    EditProfileModal,
    ProfileScore
  },
  props: {
    profile: {
      type: Object,
      required: true
    },
    account: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      editModalIsActive: false,
      editProfile: {
        mainDetails: false,
        bio: false,
        accomplishments: false,
        industries: false,
        skills: false,
        companies: false,
        education: false
      },
      isEditModeActive: false
    };
  },
  computed: {
    chunkedSkills () {
      return chunk(this.profile.skills, 2);
    },
    chunkedCompanies () {
      return chunk(this.profile.companies, 2);
    },
    topSkills () {
      if (this.profile.skills.length > 2) {
        return this.profile.skills.slice(0, 3);
      } else {
        return this.profile.skills;
      }
    },
    isUserCandidate () {
      if (!this.account) {
        return null;
      } else {
        return this.account.user_type === 'candidate';
      }
    }
  },
  methods: {
    toggleEditModal (activeSection) {
      Object.keys(this.editProfile).forEach(
        prop => (this.editProfile[prop] = false)
      );
      if (activeSection) {
        this.editProfile[activeSection] = !this.editProfile[activeSection];
      } else if (activeSection === 'saved') {
        this.editModalIsActive = !this.editModalIsActive;
      } else if (!activeSection) {
        this.$emit('refreshProfile');
      }
      this.editModalIsActive = !this.editModalIsActive;
    },
    toggleProfileEditMode () {
      this.isEditModeActive = !this.isEditModeActive;
    }
  }
};
</script>

<style lang="scss" scoped>
.title.is-6 {
  margin-bottom: 0.4rem;
}

.section {
  padding: 1rem 1.5rem;
}

@media screen and (max-width: 600px) {
  .section {
    padding: 1rem 0.75rem;
  }
}

.table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.map-marker-icon {
  margin-right: 2px;
}

td {
  padding: 1em 0.75em;
}

#top-skills > .tag {
  margin: 0.25rem 0.25rem;
}

.degree-card {
  border-radius: 4px;
  box-shadow: 0 2px 6px 0 rgba(77, 101, 117, 0.25);
  margin-bottom: 1rem;
  box-sizing: border-box;
}

.edit-icon {
  position: absolute;
  top: 1rem;
  right: 1rem;
  cursor: pointer;
}

.education-status {
  line-height: 1.5;
  .tag.is-small {
    padding: 0.5em;
    height: 1em;
    vertical-align: middle;
  }
  .small {
    text-align: bottom;
  }
}

.company-attribute {
  margin-left: 0.5rem;
}

.main-edit-profile {
  margin-right: 1rem;
}

.button.is-text {
  text-decoration: none;
}

.certification-tag {
  min-height: 0 !important;
}
</style>
